/**
 * Health and Evidence: two axes, deliberately not one.
 *
 * Before ADR-49 the codebase carried five vocabularies for the same idea, each
 * right where it was written and useless anywhere else:
 *
 *   routes/displays.ts:118        online | stale | offline
 *   web-gfx useRingProjection.ts  fresh | degraded | stale
 *   console/src/b.ts:62           ok | stale | unavailable | gone
 *   settlement_seal.degraded      a boolean
 *   RingStoreRuntimeReader        null, meaning "cannot establish truth"
 *
 * What every one of them was straining to express is that "how is it doing" and
 * "how well do we know" are different questions. Collapsing them is how a
 * dashboard ends up green because a collector threw.
 *
 * So: `Health` describes the thing. `Evidence` describes our knowledge of it.
 * The rule ADR-48 established for go-live is the rule for the whole plane:
 *
 *     Absence of evidence is never health.
 *
 * That is enforced by the shape of `Reading`, not by review. The `absent`
 * branch's health is the literal type `'unknown'`, and the `stale` branch
 * cannot be `'healthy'`, so neither "we could not reach it, call it fine" nor
 * "this reading is an hour old, call it fine" type-checks.
 */

export const HEALTH = ['healthy', 'degraded', 'failed', 'unknown'] as const;
export type Health = (typeof HEALTH)[number];

export const EVIDENCE = ['observed', 'stale', 'absent'] as const;
export type Evidence = (typeof EVIDENCE)[number];

/** A measurement that actually happened, at a known time. */
export interface Observed<T> {
  readonly evidence: 'observed';
  /** Epoch millis the value was true at (the source's clock where there is one). */
  readonly at: number;
  readonly value: T;
  readonly health: Health;
}

/**
 * We hold a previously-authoritative value that is now older than its budget.
 * Note the health type: a stale reading may be degraded, failed or unknown, and
 * may NOT be healthy. This is the `b.ts` lesson - "STALE means I have a
 * previously-authoritative value I can't currently confirm" - given teeth.
 */
export interface Stale<T> {
  readonly evidence: 'stale';
  readonly at: number;
  readonly value: T;
  readonly health: 'degraded' | 'failed' | 'unknown';
  readonly ageMs: number;
  /** How old it was allowed to be. Reporting the budget makes the call arguable. */
  readonly budgetMs: number;
}

/**
 * No measurement. There is no `value`, so nothing downstream can accidentally
 * read a default and report it as fact (engineering rule 4: never hand a model
 * a partial world without saying it was cut).
 */
export interface Absent {
  readonly evidence: 'absent';
  readonly health: 'unknown';
  /** Why we could not observe. This is the whole content of the reading. */
  readonly why: string;
}

export type Reading<T> = Observed<T> | Stale<T> | Absent;

export const observed = <T>(value: T, health: Health, at: number): Observed<T> => ({
  evidence: 'observed', at, value, health,
});

export const stale = <T>(
  value: T,
  at: number,
  ageMs: number,
  budgetMs: number,
  health: Stale<T>['health'] = 'degraded',
): Stale<T> => ({ evidence: 'stale', at, value, health, ageMs, budgetMs });

export const absent = (why: string): Absent => ({ evidence: 'absent', health: 'unknown', why });

/** True when a reading carries a value at all. Narrows for callers. */
export const hasValue = <T>(r: Reading<T>): r is Observed<T> | Stale<T> => r.evidence !== 'absent';

/**
 * Freshness against a budget, in the one place that decides it. `at` of 0 means
 * "never" (the engine's own convention for `leaseHeartbeatAt`), which is absent,
 * not infinitely stale: a lease that never beat is not a lapsed lease, it is no
 * lease at all, and the difference matters when deciding whether a ring is
 * un-provisioned or dead.
 */
export function freshness<T>(
  value: T,
  at: number,
  now: number,
  budgetMs: number,
  health: Health = 'healthy',
): Reading<T> {
  if (at <= 0) return absent('never reported');
  const age = now - at;
  if (age > budgetMs) return stale(value, at, age, budgetMs);
  return observed(value, health, at);
}

const RANK: Record<Health, number> = { failed: 3, degraded: 2, unknown: 1, healthy: 0 };

/**
 * Roll many healths into one. Worst wins, and `unknown` outranks `healthy` on
 * purpose: a system with one blind spot is not a healthy system, it is a system
 * with one blind spot. It ranks BELOW degraded because a known problem is more
 * actionable than an unknown one, and the ordering only decides which single
 * word a summary shows - the findings carry the detail either way.
 *
 * An empty rollup is `unknown`, never `healthy`. Nothing observed is not health.
 */
export function rollup(healths: Iterable<Health>): Health {
  let worst: Health | null = null;
  for (const h of healths) if (worst === null || RANK[h] > RANK[worst]) worst = h;
  return worst ?? 'unknown';
}

/** Rollup over readings, which is the common case. */
export const rollupReadings = (readings: Iterable<Reading<unknown>>): Health =>
  rollup([...readings].map((r) => r.health));

// ---------------------------------------------------------------------------
// The safety property, asserted where it is actually checked.
//
// These live in src/ rather than in the test suite on purpose: no package in
// this repo puts `test/` in its tsconfig `include`, and vitest transpiles
// without typechecking, so a `@ts-expect-error` in a test file is decorative -
// it would keep passing after the property it guards was deleted.
//
// If either of these stops erroring, `Reading` has lost its teeth and this
// file fails to compile.
// ---------------------------------------------------------------------------

// @ts-expect-error an unobserved reading may not claim health
const _absentCannotBeHealthy: Reading<number> = { evidence: 'absent', health: 'healthy', why: 'unreachable' };
// @ts-expect-error a value past its freshness budget may not claim health
const _staleCannotBeHealthy: Reading<number> = { evidence: 'stale', at: 1, value: 1, health: 'healthy', ageMs: 9, budgetMs: 1 };
void _absentCannotBeHealthy;
void _staleCannotBeHealthy;
